import { useState, useMemo, useEffect } from 'react';
import { useFrequenciaTurma, useConsolidacao } from '../../api/hooks/useRelatorios';
import type { FrequenciaAluno } from '../../api/hooks/useRelatorios';
import { useProfessorTurmas } from '../../api/hooks/useProfessorTurmas';
import { useAuthStore } from '../../store/authStore';
import { ANO_LETIVO_ATUAL } from './relatorios-professor-shared';

export function useRelatoriosProfessorPage() {
  const user = useAuthStore((s) => s.user);
  const escolaId = useAuthStore((s) => s.escolaId);

  const { turmas, isLoading: loadingGrade } = useProfessorTurmas(user?.id);

  const minhasTurmas = useMemo(
    () =>
      turmas.map((t) => ({
        id: t.id,
        nome: t.nome,
        serie: t.serie,
      })),
    [turmas],
  );

  const [selectedTurmaId, setSelectedTurmaId] = useState('');

  // Auto-seleciona a primeira turma ativa para evitar tela vazia desnecessária
  useEffect(() => {
    if (!selectedTurmaId && minhasTurmas.length > 0) {
      setSelectedTurmaId(minhasTurmas[0].id);
    }
  }, [minhasTurmas, selectedTurmaId]);
  const [selectedAlunoId, setSelectedAlunoId] = useState<string | null>(null);
  const [anoLetivo] = useState(ANO_LETIVO_ATUAL);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [somenteRisco, setSomenteRisco] = useState(false);

  const freqQuery = useFrequenciaTurma(
    escolaId || undefined,
    selectedTurmaId || undefined,
    anoLetivo,
  );
  const freq = freqQuery.data;

  const consQuery = useConsolidacao(escolaId || undefined, anoLetivo);
  const cons = consQuery.data;

  const alunosExibidos = useMemo(() => {
    if (!freq?.alunos) return [];
    const list = somenteRisco ? freq.alunos.filter((a) => a.emRisco) : [...freq.alunos];
    return list.sort((a, b) => a.percentual - b.percentual);
  }, [freq?.alunos, somenteRisco]);

  const alunoSelecionado = useMemo(
    () =>
      selectedAlunoId
        ? alunosExibidos.find((a) => a.alunoId === selectedAlunoId) ?? null
        : null,
    [selectedAlunoId, alunosExibidos],
  );

  const turmaSelecionada = useMemo(
    () => minhasTurmas.find((t) => t.id === selectedTurmaId) ?? null,
    [minhasTurmas, selectedTurmaId],
  );

  return {
    escolaId,
    minhasTurmas,
    selectedTurmaId,
    setSelectedTurmaId,
    selectedAlunoId,
    setSelectedAlunoId,
    alunoSelecionado,
    turmaSelecionada,
    anoLetivo,
    filtrosAbertos,
    setFiltrosAbertos,
    somenteRisco,
    setSomenteRisco,
    freq,
    freqQuery,
    cons,
    consQuery,
    alunosExibidos,
    isLoading: loadingGrade || freqQuery.isLoading,
    isLoadingCons: consQuery.isLoading,
  };
}

export type RelatoriosProfessorPageContext = ReturnType<typeof useRelatoriosProfessorPage>;

export type { FrequenciaAluno };
